import { formatWhen } from "@/components/boo/access-copy";
import { InlineError } from "@/components/QueryState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, type BillingAddress, type BillingDetails } from "@/lib/api";
import { useSession } from "@/lib/auth-client";
import { countryName } from "@/lib/countries";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { type ComponentProps, useState } from "react";
import { toast } from "sonner";
import { CountryPicker } from "./CountryPicker";

// The "Bill to" block of every invoice: the legal company name (the org name
// unless set) and the billing address. Reads as a card; Edit opens the form.
// Leads the Usage page's Invoices tab, which only owners, admins and billing admins get;
// the backend decides who may edit (_permissions.canEditBillingDetails).

type AddressForm = { line1: string; line2: string; city: string; state: string; postalCode: string; country: string };
type AddressField = keyof AddressForm;
const EMPTY_ADDRESS: AddressForm = { line1: "", line2: "", city: "", state: "", postalCode: "", country: "" };

function addressToForm(a: BillingAddress | null): AddressForm {
  return a
    ? { line1: a.line1, line2: a.line2 ?? "", city: a.city, state: a.state ?? "", postalCode: a.postalCode, country: a.country }
    : EMPTY_ADDRESS;
}

const REQUIRED: { field: AddressField; error: string }[] = [
  { field: "line1", error: "Enter the street address." },
  { field: "city", error: "Enter a city." },
  { field: "postalCode", error: "Enter a postal code." },
  { field: "country", error: "Choose a country." },
];

// All blank = no address. Otherwise every required field must be filled.
function formToAddress(f: AddressForm): { address: BillingAddress | null; errors: Partial<Record<AddressField, string>> } {
  const t = Object.fromEntries((Object.keys(f) as AddressField[]).map((k) => [k, f[k].trim()])) as AddressForm;
  if (!Object.values(t).some(Boolean)) return { address: null, errors: {} };
  const errors: Partial<Record<AddressField, string>> = {};
  for (const r of REQUIRED) if (!t[r.field]) errors[r.field] = r.error;
  if (Object.keys(errors).length) return { address: null, errors };
  return {
    address: { line1: t.line1, line2: t.line2 || null, city: t.city, state: t.state || null, postalCode: t.postalCode, country: t.country },
    errors: {},
  };
}

function addressLines(a: BillingAddress): [string, string] {
  const street = [a.line1, a.line2].filter(Boolean).join(", ");
  const locality = [a.city, [a.state, a.postalCode].filter(Boolean).join(" ")].filter(Boolean).join(", ");
  return [street, [locality, countryName(a.country)].filter(Boolean).join(", ")];
}

export function InvoiceDetailsSection() {
  const { data: session } = useSession();
  const organizationId = session?.session.activeOrganizationId ?? null;
  const queryClient = useQueryClient();
  const key = ["billing-details", organizationId];
  const query = useQuery({ queryKey: key, queryFn: () => api.billing.details(), enabled: !!organizationId });
  const [editing, setEditing] = useState(false);

  return (
    <section className="space-y-3">
      <div>
        <h2 className="text-base font-semibold">Invoice details</h2>
        <p className="max-w-xl text-sm text-muted-foreground text-pretty">
          The company name and billing address in the "Bill to" block of your invoices. Used on invoices for future credit
          purchases and auto-recharges. Invoices already issued don't change.
        </p>
      </div>
      {query.isLoading ? (
        <div className="h-24" aria-busy />
      ) : query.isError || !query.data ? (
        <InlineError error={query.error ?? "Failed to load invoice details"} />
      ) : editing ? (
        <BillingDetailsForm
          details={query.data}
          onSaved={(next) => {
            queryClient.setQueryData(key, next);
            setEditing(false);
          }}
          onCancel={() => setEditing(false)}
        />
      ) : (
        <BillingDetailsCard details={query.data} onEdit={() => setEditing(true)} />
      )}
    </section>
  );
}

function BillingDetailsCard({ details, onEdit }: { details: BillingDetails; onEdit: () => void }) {
  const canEdit = details._permissions?.canEditBillingDetails === true;
  const hasAny = details.name !== null || details.address !== null;

  if (!hasAny) {
    return (
      <div className="flex max-w-xl flex-wrap items-center justify-between gap-3 rounded-lg border border-dashed p-4">
        <p className="text-sm text-muted-foreground text-pretty">
          No billing address yet. Add one so your invoices show your company name and address.
        </p>
        {canEdit ? (
          <Button variant="outline" size="sm" onClick={onEdit}>
            Add billing address
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">Only owners, admins and billing admins can add one.</p>
        )}
      </div>
    );
  }

  const lines = details.address ? addressLines(details.address) : null;
  return (
    <div className="max-w-xl space-y-2">
      <div className="flex items-start justify-between gap-4 rounded-lg border p-4">
        <div className="text-sm">
          <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">Bill to</div>
          <div className="mt-1 font-medium">{details.name ?? details.orgName}</div>
          {lines && (
            <div className="text-muted-foreground">
              <div>{lines[0]}</div>
              <div>{lines[1]}</div>
            </div>
          )}
        </div>
        {canEdit && (
          <Button variant="outline" size="sm" onClick={onEdit}>
            Edit
          </Button>
        )}
      </div>
      {details.updatedAt && details.updatedBy && (
        <p className="text-xs text-muted-foreground">
          Changed by {details.updatedBy.name}, {formatWhen(details.updatedAt)}.
        </p>
      )}
      {!canEdit && <p className="text-xs text-muted-foreground">Only owners, admins and billing admins can change this.</p>}
    </div>
  );
}

function BillingDetailsForm({
  details,
  onSaved,
  onCancel,
}: {
  details: BillingDetails;
  onSaved: (next: BillingDetails) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState(details.name ?? "");
  const [address, setAddress] = useState<AddressForm>(addressToForm(details.address));
  const [errors, setErrors] = useState<Partial<Record<AddressField, string>>>({});

  const save = useMutation({
    mutationFn: (body: { name: string | null; address: BillingAddress | null }) => api.billing.updateDetails(body),
    onSuccess: (next, body) => {
      onSaved(next);
      toast.success(body.address === null && details.address ? "Billing address removed" : "Invoice details saved");
    },
  });

  const set = (field: AddressField) => (value: string) => {
    setAddress((a) => ({ ...a, [field]: value }));
    if (errors[field]) setErrors((e) => ({ ...e, [field]: undefined }));
  };
  const submit = () => {
    const { address: parsed, errors: next } = formToAddress(address);
    setErrors(next);
    if (Object.keys(next).length) return;
    save.mutate({ name: name.trim() || null, address: parsed });
  };
  const removeAddress = () => save.mutate({ name: name.trim() || null, address: null });

  const field = (id: AddressField, label: string, extra: Partial<ComponentProps<typeof Input>> = {}) => (
    <div className="grid gap-2">
      <Label htmlFor={`billing-${id}`}>{label}</Label>
      <Input
        id={`billing-${id}`}
        value={address[id]}
        onChange={(e) => set(id)(e.target.value)}
        aria-invalid={errors[id] ? true : undefined}
        {...extra}
      />
      {errors[id] && <p className="text-xs text-destructive">{errors[id]}</p>}
    </div>
  );

  return (
    <form
      className="grid max-w-xl gap-4 rounded-lg border p-4"
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
    >
      <div className="grid gap-2">
        <Label htmlFor="billing-name">Legal company name</Label>
        <Input id="billing-name" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} autoComplete="organization" />
        <p className="text-xs text-muted-foreground">Leave blank to use your organization name, "{details.orgName}".</p>
      </div>
      {field("line1", "Address line 1", { maxLength: 200, autoComplete: "address-line1" })}
      {field("line2", "Address line 2 (optional)", { maxLength: 200, autoComplete: "address-line2" })}
      <div className="grid gap-4 sm:grid-cols-2">
        {field("city", "City", { maxLength: 100, autoComplete: "address-level2" })}
        {field("state", "State or province (optional)", { maxLength: 100, autoComplete: "address-level1" })}
        {field("postalCode", "Postal code", { maxLength: 20, autoComplete: "postal-code" })}
        <div className="grid gap-2">
          <Label htmlFor="billing-country">Country</Label>
          <CountryPicker id="billing-country" value={address.country} onChange={set("country")} invalid={!!errors.country} />
          {errors.country && <p className="text-xs text-destructive">{errors.country}</p>}
        </div>
      </div>
      {save.isError && <InlineError error={save.error} />}
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" loading={save.isPending}>
          Save
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={save.isPending}>
          Cancel
        </Button>
        {details.address && (
          <Button type="button" variant="ghost" className="ml-auto text-destructive" onClick={removeAddress} disabled={save.isPending}>
            Remove address
          </Button>
        )}
      </div>
    </form>
  );
}
